import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ModalRole } from '../models/modal-roles';
import { StateService } from '../../services/state.service';
import { ToolService } from '../../services/tool.service';
import { User, UserRegistration, UserType } from '../../api/models';

@Component({
  selector: 'lib-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  standalone: false,
})
export class UserFormComponent implements OnInit {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public tools: ToolService,
    public platform: Platform,
    private modalController: ModalController
  ) {}
  userTypes = (Object.values(UserType) as number[]).filter(
    (v) =>
      typeof v === 'number' &&
      (v !== UserType.$3 || this.state.isApplicationAdmin) &&
      (v !== UserType.$2 || this.state.isCompanyAdmin)
  );

  private subs: Subscription[] = [];
  @Input() userToEdit: User;
  public user: UserRegistration = {};
  public isEdit: boolean = false;
  public showPassword: boolean = false;
  public allUsers: User[] = [];

  ngOnInit() {
    this.subs.push(this.state.users$.subscribe((x) => (this.allUsers = x)));
    if (this.userToEdit != null) {
      this.isEdit = true;
      this.user.userName = this.userToEdit.userName;
      this.user.recievesFixedSalary = this.userToEdit.recievesFixedSalary;
      this.user.firstName = this.userToEdit.firstName;
      this.user.lastName = this.userToEdit.lastName;
      this.user.personalNumber = this.userToEdit.personalNumber;
      this.user.password = '';
      this.user.type = this.userToEdit.type;
      this.user.isDeactivated = this.userToEdit.isDeactivated;
      this.user.deactivatedUtc = this.userToEdit.deactivatedUtc;
      this.user.companies = this.userToEdit.companies;
      this.user.workingHoursPerDay = this.userToEdit.workingHoursPerDay;
    } else {
      this.user.firstName = '';
      this.user.lastName = '';
      this.user.workingHoursPerDay = 8;
      this.user.type = UserType.$0;
      this.generatePassword();
    }
  }

  getUserTypeLabel(type: UserType): string {
    switch (type) {
      case UserType.$0:
        return this.translate.instant('user.type.employee');
      case UserType.$1:
        return this.translate.instant('user.type.office');
      case UserType.$2:
        return this.translate.instant('user.type.companyAdmin');
      case UserType.$3:
        return this.translate.instant('user.type.appAdmin');
      default:
        return this.translate.instant('user.type.unknown');
    }
  }

  public cancel() {
    this.modalController.dismiss(null, ModalRole.Cancel);
  }

  public registerUser() {
    this.tools
      .showConfirmAlert(
        false,
        null,
        this.translate.instant('user.confirmCreate')
      )
      .then((x) => {
        if (x) {
          this.state.createUser(
            this.user.firstName,
            this.user.lastName,
            this.user.password,
            this.user.personalNumber,
            this.user.type,
            this.user.recievesFixedSalary
          );
          this.modalController.dismiss();
        }
      });
  }

  userExists() {
    if (
      this.allUsers.find(
        (x) =>
          x.firstName.toLowerCase() ==
            this.user.firstName.trim().toLowerCase() &&
          x.lastName.toLowerCase() == this.user.lastName.toLowerCase()
      ) != null
    )
      return true;
    return false;
  }

  updateUser() {
    this.state.updateUser(this.user);
    this.modalController.dismiss();
  }

  deleteUser() {
    this.tools
      .showConfirmAlert(
        true,
        this.translate.instant('user.confirmDelete', {
          name: `${this.user.firstName} ${this.user.lastName}`,
        })
      )
      .then((x) => {
        if (x) this.state.deleteUser(this.user.userName);
        this.modalController.dismiss();
      });
  }

  deactivateUser() {
    this.tools
      .showConfirmAlert(
        false,
        this.translate.instant('user.deactivateWarning'),
        this.translate.instant('user.confirmDeactivate')
      )
      .then((x) => {
        if (x) this.state.deactivateUser(this.user.userName);
        this.modalController.dismiss();
      });
  }

  activateUser() {
    this.tools
      .showConfirmAlert(
        false,
        this.translate.instant('user.activateWarning'),
        this.translate.instant('user.confirmActivate')
      )
      .then((x) => {
        if (x) this.state.activateUser(this.user.userName);
        this.modalController.dismiss();
      });
  }

  hasInvalidFields(): boolean {
    if (
      this.user.firstName == '' ||
      this.user.lastName == '' ||
      (!this.isValidPw(this.user.password) && !this.isEdit) ||
      (this.isEdit && this.showPassword && !this.isValidPw(this.user.password))
    ) {
      return true;
    } else {
      return false;
    }
  }

  generatePassword(length: number = 12): void {
    this.showPassword = true;
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const numberChars = '0123456789';
    this.user.password =
      Array.from({ length }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length))
      ).join('') +
      numberChars.charAt(Math.floor(Math.random() * numberChars.length));
  }

  isValidPw(input: string): boolean {
    if (input.length < 12) {
      return false;
    }
    const hasLetter = /[A-Za-z]/.test(input);
    const hasNumber = /\d/.test(input);
    return hasLetter && hasNumber;
  }
}
